import React, { useEffect, useRef, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  /** Optional decorative icon rendered before the title text (e.g. <Key /> for an API-key modal). Aria-hidden — the title text carries semantic meaning. */
  titleIcon?: React.ReactNode;
  description?: string;
  children: React.ReactNode;
  /** Controls dialog width. Default 'md' (max-w-md). */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full';
  /** Hide the default close (X) button. */
  hideCloseButton?: boolean;
  /** Disable backdrop click to close. Useful while a submit is in flight. */
  dismissOnBackdrop?: boolean;
  /** Disable Escape key to close. Useful while a submit is in flight. */
  dismissOnEscape?: boolean;
  /** Optional footer rendered in its own section. */
  footer?: React.ReactNode;
  /** Optional className appended to dialog panel. */
  className?: string;
  /** Stacking level: each nested dialog should bump up. Defaults to 100. */
  zIndex?: number;
  /** When true, the dialog fills the height of the screen (useful for long forms). */
  fullHeight?: boolean;
  /** Hide the dialog header (title/description/close row). */
  hideHeader?: boolean;
  /**
   * Where the dialog sits on screen.
   * - 'center' (default): traditional modal — centered with margins on all sides
   * - 'right': drawer-style — slides against the right edge, full viewport height
   */
  placement?: 'center' | 'right';
}

const SIZE_MAP: Record<NonNullable<DialogProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  full: 'max-w-[min(100vw-2rem,1200px)]',
};

/**
 * Width mapping for placement="right" — drawers are wider on average than
 * centered modals. Most settings panels in the app use ~600px which is
 * between xl (576px) and 2xl (672px); xl is the practical default for
 * right-aligned drawers.
 */
const RIGHT_SIZE_MAP: Record<NonNullable<DialogProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-[600px]',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  full: 'max-w-[min(100vw,1200px)]',
};

/**
 * FOCUSABLE_SELECTOR — elements we want to trap focus between.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Accessible Dialog primitive.
 *
 * Features:
 * - Focus is trapped inside while open.
 * - Escape closes (unless dismissOnEscape is false).
 * - Backdrop click closes (unless dismissOnBackdrop is false).
 * - role="dialog" + aria-modal + labelled by title.
 * - Body scroll is locked while any dialog is open.
 * - Previously focused element is restored on close.
 * - Renders via React portal so it escapes parent overflow.
 *
 * Prefer this over custom modal scaffolding.
 */
const Dialog: React.FC<DialogProps> = ({
  isOpen,
  onClose,
  title,
  titleIcon,
  description,
  children,
  size = 'md',
  hideCloseButton = false,
  dismissOnBackdrop = true,
  dismissOnEscape = true,
  footer,
  className = '',
  zIndex = 100,
  fullHeight = false,
  hideHeader = false,
  placement = 'center',
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Focus trap + initial focus + restore focus on close.
  useEffect(() => {
    if (!isOpen) return;

    previouslyFocused.current = document.activeElement as HTMLElement;

    const focusInitial = () => {
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter((el) => !el.hasAttribute('data-dialog-skip-focus'));
      if (focusables.length > 0) {
        focusables[0].focus();
      } else {
        panel.focus();
      }
    };

    // Defer focus so child components can render first.
    const t = setTimeout(focusInitial, 0);
    return () => clearTimeout(t);
  }, [isOpen]);

  // Restore focus when closing.
  useEffect(() => {
    if (isOpen) return;
    const prev = previouslyFocused.current;
    if (prev && typeof prev.focus === 'function') {
      try { prev.focus(); } catch { /* ignore */ }
    }
  }, [isOpen]);

  // Body scroll lock.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  // Key handling: Escape + Tab focus trap.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissOnEscape) {
        e.stopPropagation();
        handleClose();
        return;
      }
      if (e.key === 'Tab') {
        const panel = panelRef.current;
        if (!panel) return;
        const focusables = Array.from(
          panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
        ).filter((el) => el.offsetParent !== null);
        if (focusables.length === 0) {
          e.preventDefault();
          panel.focus();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [isOpen, dismissOnEscape, handleClose]);

  if (!isOpen) return null;

  const isRight = placement === 'right';

  // Panel positioning + size differ between center and right placements.
  // - center: relative + width cap + max-height + rounded-xl
  //           (margins around all sides, rounded corners)
  // - right:  absolute inset-y-0 right-0 + width cap + NO rounded corners
  //           (flush against viewport edges — matches the established
  //           settings-panel drawer look. Rounded corners on a full-bleed
  //           drawer create a visible "tab" against the viewport edge and
  //           expose page chrome during rubber-band scrolling.)
  const panelClasses = isRight
    ? `absolute inset-y-0 right-0 w-full ${RIGHT_SIZE_MAP[size]} bg-ink-50 shadow-2xl flex flex-col outline-none`
    : `relative w-full ${SIZE_MAP[size]} bg-ink-50 rounded-xl shadow-2xl flex flex-col outline-none ${
        fullHeight ? 'max-h-[calc(100dvh-2rem)] h-[calc(100dvh-2rem)]' : 'max-h-[calc(100dvh-2rem)]'
      }`;

  const content = (
    <div
      className={isRight ? 'fixed inset-0' : 'fixed inset-0 flex items-center justify-center p-4'}
      style={{ zIndex }}
      role="presentation"
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
        onClick={dismissOnBackdrop ? handleClose : undefined}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={`${panelClasses} ${className}`}
      >
        {!hideHeader && (title || !hideCloseButton) && (
          <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-ink-100">
            <div className="flex-1 min-w-0">
              {title && (
                <div className="flex items-center gap-2">
                  {titleIcon && (
                    <span className="flex-shrink-0 text-ink-700" aria-hidden="true">
                      {titleIcon}
                    </span>
                  )}
                  <h2
                    id={titleId}
                    className="text-15 font-semibold text-ink-900 truncate"
                  >
                    {title}
                  </h2>
                </div>
              )}
              {description && (
                <p id={descriptionId} className="text-13 text-ink-500 mt-0.5">
                  {description}
                </p>
              )}
            </div>
            {!hideCloseButton && (
              <button
                type="button"
                onClick={handleClose}
                className="flex-shrink-0 p-1.5 -m-1.5 rounded-md text-ink-400 hover:text-ink-700 hover:bg-ink-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/40 transition-colors"
                aria-label="Close dialog"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>

        {footer && (
          <div className="flex-shrink-0 px-5 py-3 border-t border-ink-100 bg-ink-100/60 rounded-b-xl">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default Dialog;
